// Rotas do jogo. Sem biblioteca: a história do navegador basta.
import { useLayoutEffect, useState } from 'react';
import { type Ciclo, type Plano, type PlanoPago, ehCiclo, ehIngresso, ehPlano, ehPlanoPago } from '@truco6/planos';
import { ORIGENS_DA_VAGA, type OrigemDaVaga } from '@truco6/mesa/protocolo';

export type Rota =
  | { tela: 'inicio'; aba?: 'abertas' | 'tentos' } // a aba Jogar, com a lista de salas abertas (/salas abre na aba pedida)
  | { tela: 'liga'; trio: string | null } // procurando mesa na Liga da Semana, sozinho ou com um trio fixo
  | { tela: 'ranking'; aba?: 'comunidades' | 'campeonatos'; tipo?: 'pote' } // a aba Ligas: Liga da Semana (ranking), Comunidades ou Campeonatos; tipo: abre no Rei do Pote
  | { tela: 'novo-campeonato'; comunidade?: string } // /campeonatos/novo, solto ou na comunidade
  | { tela: 'campeonato'; slug: string; aba?: AbaDoCampeonato } // a página do campeonato: /campeonato/<slug>
  | { tela: 'nova-comunidade' }
  | { tela: 'comunidade'; id: string; aba?: AbaDaComunidade } // /comunidades/ID
  | { tela: 'convite-comunidade'; codigo: string } // link de convite da comunidade: /comunidade/CODIGO
  | { tela: 'conquistas'; badge: string | null } // ?badge=ID abre o detalhe
  | { tela: 'missoes' } // missões do dia e da semana e o baú
  | { tela: 'colecao' } // cosméticos: mesa, verso, moldura e a loja
  | { tela: 'passe' } // Passe da Temporada
  | { tela: 'trios' }
  | { tela: 'novo-trio'; convidar: string | null } // "Criar trio novo com Léo": o convite sai depois de criar
  | { tela: 'trio'; id: string }
  | { tela: 'convite-trio'; codigo: string } // link do convite de trio: /t/CODIGO
  | { tela: 'sala'; codigo: string; vaga?: OrigemDaVaga } // vaga: chegou para sentar na vaga aberta (lista, amigo...)
  | { tela: 'assistir'; codigo: string | null; amigo: string | null } // plateia: pelo código (lista, link) ou pelo amigo (aba Amigos)
  | { tela: 'perfil'; campo?: 'cidade' } // campo: abre na cidade (ESCOLHER CIDADE do ranking)
  | { tela: 'estatisticas' } // estatísticas por plano, a partir do seu perfil
  | { tela: 'marcador' } // marcador presencial: placar ao vivo (Prata e Ouro) ou só no aparelho
  | { tela: 'noites' } // histórico das noites do marcador
  | { tela: 'placar'; codigo: string; caneta: string | null; telao: boolean } // placar ao vivo, aberto sem conta
  | { tela: 'notificacoes' }
  | { tela: 'amigos' }
  | { tela: 'configuracoes' }
  | { tela: 'jogador'; id: string } // perfil público: /jogador/ID
  | { tela: 'nova-sala'; ingresso: number | null; comunidade?: string } // com ingresso: abre em "valendo Tentos"; comunidade: mesa só dos membros
  | { tela: 'planos'; plano: Plano | null; ciclo: Ciclo }
  | { tela: 'assinar'; plano: PlanoPago; ciclo: Ciclo }
  | { tela: 'assinatura-ok'; plano: PlanoPago | null }
  | { tela: 'meu-plano' }
  | { tela: 'indique' }
  | { tela: 'como-jogar' } // as regras da mesa, a partir do Perfil
  | { tela: 'termos' }
  | { tela: 'privacidade' }
  | { tela: 'limite' }
  | { tela: 'indicacao'; codigo: string } // link de quem indicou: /c/CODIGO
  | { tela: 'simulado'; sessao: string | null; fatura: string | null } // Stripe simulada, só no modo local
  | { tela: 'loja' }
  | { tela: 'extrato' }
  | { tela: 'compra-ok'; sessao: string | null }
  | { tela: 'compra-simulada'; sessao: string | null }; // checkout de Tentos simulado, só no modo local

export const ABAS_DA_COMUNIDADE = ['mural', 'mesas', 'campeonatos', 'ranking', 'eventos', 'membros'] as const;
export type AbaDaComunidade = (typeof ABAS_DA_COMUNIDADE)[number];
const ehAbaDaComunidade = (x: string | null): x is AbaDaComunidade => (ABAS_DA_COMUNIDADE as readonly (string | null)[]).includes(x);
export const ABAS_DO_CAMPEONATO = ['tabela', 'jogos', 'trios', 'organizar'] as const;
export type AbaDoCampeonato = (typeof ABAS_DO_CAMPEONATO)[number];
const ehAbaDoCampeonato = (x: string | null): x is AbaDoCampeonato => (ABAS_DO_CAMPEONATO as readonly (string | null)[]).includes(x);
const ID = /^[0-9a-f-]{36}$/;

export function lerRota(caminho: string, busca = ''): Rota {
  const q = new URLSearchParams(busca);
  const plano = q.get('plano'), cicloBruto = q.get('ciclo');
  const ciclo: Ciclo = ehCiclo(cicloBruto) ? cicloBruto : 'mensal';
  const m = /^\/s\/([A-Za-z2-9]{6})\/?$/.exec(caminho);
  if (m) {
    const vaga = q.get('vaga');
    return { tela: 'sala', codigo: m[1]!.toUpperCase(), ...((ORIGENS_DA_VAGA as readonly string[]).includes(vaga ?? '') ? { vaga: vaga as OrigemDaVaga } : {}) };
  }
  // /sala/CODIGO: o mesmo convite de /s/CODIGO (é o endereço que a prévia do WhatsApp mostra).
  const sl = /^\/sala\/([A-Za-z2-9]{6})\/?$/.exec(caminho);
  if (sl) return { tela: 'sala', codigo: sl[1]!.toUpperCase() };
  const as = /^\/assistir(?:\/([A-Za-z2-9]{6}))?\/?$/.exec(caminho);
  if (as) {
    const amigo = q.get('amigo');
    return { tela: 'assistir', codigo: as[1]?.toUpperCase() ?? null, amigo: !as[1] && amigo && /^[A-Za-z0-9_-]{1,64}$/.test(amigo) ? amigo : null };
  }
  const c = /^\/c\/([A-Za-z2-9]{6})\/?$/.exec(caminho);
  if (c) return { tela: 'indicacao', codigo: c[1]!.toUpperCase() };
  const t = /^\/t\/([A-Za-z2-9]{6})\/?$/.exec(caminho);
  if (t) return { tela: 'convite-trio', codigo: t[1]!.toUpperCase() };
  const pl = /^\/placar\/([A-HJ-NP-Z2-9]{8})\/?$/.exec(caminho);
  if (pl) {
    const caneta = q.get('caneta');
    return { tela: 'placar', codigo: pl[1]!, caneta: caneta && /^[A-Za-z0-9_-]{16,64}$/.test(caneta) ? caneta : null, telao: q.get('telao') === '1' };
  }
  const jg = /^\/jogador\/([A-Za-z0-9_-]{1,64})\/?$/.exec(caminho);
  if (jg) return { tela: 'jogador', id: jg[1]! };
  const cm = /^\/comunidades\/([0-9a-f-]{36})\/?$/.exec(caminho);
  if (cm) { const aba = q.get('aba'); return { tela: 'comunidade', id: cm[1]!, ...(ehAbaDaComunidade(aba) && aba !== 'mural' ? { aba } : {}) }; }
  const cc = /^\/comunidade\/([A-HJ-NP-Za-hj-np-z2-9]{8})\/?$/.exec(caminho);
  if (cc) return { tela: 'convite-comunidade', codigo: cc[1]!.toUpperCase() };
  const ca = /^\/campeonato\/([a-z0-9-]{3,60})\/?$/.exec(caminho);
  if (ca) { const aba = q.get('aba'); return { tela: 'campeonato', slug: ca[1]!, ...(ehAbaDoCampeonato(aba) && aba !== 'tabela' ? { aba } : {}) }; }
  const tr = /^\/trios\/([0-9a-f-]{8,40})\/?$/.exec(caminho);
  if (tr) return { tela: 'trio', id: tr[1]! };
  switch (caminho.replace(/\/+$/, '')) {
    case '/perfil': return q.get('campo') === 'cidade' ? { tela: 'perfil', campo: 'cidade' } : { tela: 'perfil' };
    case '/estatisticas': return { tela: 'estatisticas' };
    case '/marcador': return { tela: 'marcador' };
    case '/marcador/noites': return { tela: 'noites' };
    case '/notificacoes': return { tela: 'notificacoes' };
    case '/amigos': return { tela: 'amigos' };
    case '/configuracoes': return { tela: 'configuracoes' };
    case '/liga': { const trio = q.get('trio'); return { tela: 'liga', trio: trio && /^[0-9a-f-]{8,40}$/.test(trio) ? trio : null }; }
    case '/ranking': return q.get('tipo') === 'pote' ? { tela: 'ranking', tipo: 'pote' } : { tela: 'ranking' };
    case '/comunidades': return { tela: 'ranking', aba: 'comunidades' };
    case '/comunidades/nova': return { tela: 'nova-comunidade' };
    case '/campeonatos': return { tela: 'ranking', aba: 'campeonatos' };
    case '/campeonatos/novo': { const c = q.get('comunidade'); return { tela: 'novo-campeonato', ...(c && ID.test(c) ? { comunidade: c } : {}) }; }
    case '/missoes': return { tela: 'missoes' };
    case '/colecao': return { tela: 'colecao' };
    case '/passe': return { tela: 'passe' };
    case '/conquistas': { const b = q.get('badge'); return { tela: 'conquistas', badge: b && /^[a-z0-9_]{1,40}$/.test(b) ? b : null }; }
    case '/trios': return { tela: 'trios' };
    case '/trios/novo': { const c = q.get('convidar'); return { tela: 'novo-trio', convidar: c && /^[A-Za-z0-9_-]{1,64}$/.test(c) ? c : null }; }
    case '/sala/nova': {
      const ingresso = Number(q.get('ingresso')), comunidade = q.get('comunidade');
      return { tela: 'nova-sala', ingresso: ehIngresso(ingresso) ? ingresso : null, ...(comunidade && ID.test(comunidade) ? { comunidade } : {}) };
    }
    // A lista de salas fica na aba Jogar: /salas abre ali, na aba pedida.
    case '/salas': return q.get('aba') === 'tentos' ? { tela: 'inicio', aba: 'tentos' } : { tela: 'inicio' };
    case '/planos': return { tela: 'planos', plano: ehPlano(plano) ? plano : null, ciclo };
    case '/assinar':
    case '/planos/assinar': return ehPlanoPago(plano) ? { tela: 'assinar', plano, ciclo } : { tela: 'planos', plano: null, ciclo };
    case '/planos/ok': return { tela: 'assinatura-ok', plano: ehPlanoPago(plano) ? plano : null };
    case '/planos/simulado': return { tela: 'simulado', sessao: q.get('sessao'), fatura: q.get('fatura') };
    case '/conta/plano': return { tela: 'meu-plano' };
    case '/indique': return { tela: 'indique' };
    case '/como-jogar': return { tela: 'como-jogar' };
    case '/termos': return { tela: 'termos' };
    case '/privacidade': return { tela: 'privacidade' };
    case '/limite': return { tela: 'limite' };
    case '/tentos': return { tela: 'loja' };
    case '/tentos/extrato': return { tela: 'extrato' };
    case '/tentos/ok': return { tela: 'compra-ok', sessao: q.get('sessao') };
    case '/tentos/simulado': return { tela: 'compra-simulada', sessao: q.get('sessao') };
    default: return { tela: 'inicio' };
  }
}

export function caminhoDa(r: Rota): string {
  switch (r.tela) {
    case 'sala': return `/s/${r.codigo}` + (r.vaga ? `?vaga=${r.vaga}` : '');
    case 'assistir': return r.codigo ? `/assistir/${r.codigo}` : r.amigo ? `/assistir?amigo=${encodeURIComponent(r.amigo)}` : '/assistir';
    case 'perfil': return r.campo ? `/perfil?campo=${r.campo}` : '/perfil';
    case 'estatisticas': return '/estatisticas';
    case 'marcador': return '/marcador';
    case 'noites': return '/marcador/noites';
    case 'placar': {
      const q = new URLSearchParams();
      if (r.caneta) q.set('caneta', r.caneta);
      if (r.telao) q.set('telao', '1');
      return `/placar/${r.codigo}` + (q.size ? `?${q}` : '');
    }
    case 'notificacoes': return '/notificacoes';
    case 'amigos': return '/amigos';
    case 'configuracoes': return '/configuracoes';
    case 'jogador': return `/jogador/${r.id}`;
    case 'liga': return r.trio ? `/liga?trio=${r.trio}` : '/liga';
    case 'ranking': return r.aba === 'comunidades' ? '/comunidades' : r.aba === 'campeonatos' ? '/campeonatos' : r.tipo === 'pote' ? '/ranking?tipo=pote' : '/ranking';
    case 'novo-campeonato': return r.comunidade ? `/campeonatos/novo?comunidade=${r.comunidade}` : '/campeonatos/novo';
    case 'campeonato': return `/campeonato/${r.slug}` + (r.aba && r.aba !== 'tabela' ? `?aba=${r.aba}` : '');
    case 'nova-comunidade': return '/comunidades/nova';
    case 'comunidade': return `/comunidades/${r.id}` + (r.aba && r.aba !== 'mural' ? `?aba=${r.aba}` : '');
    case 'convite-comunidade': return `/comunidade/${r.codigo}`;
    case 'missoes': return '/missoes';
    case 'colecao': return '/colecao';
    case 'passe': return '/passe';
    case 'conquistas': return r.badge ? `/conquistas?badge=${r.badge}` : '/conquistas';
    case 'trios': return '/trios';
    case 'novo-trio': return r.convidar ? `/trios/novo?convidar=${encodeURIComponent(r.convidar)}` : '/trios/novo';
    case 'trio': return `/trios/${r.id}`;
    case 'convite-trio': return `/t/${r.codigo}`;
    case 'nova-sala': {
      const q = new URLSearchParams();
      if (r.ingresso) q.set('ingresso', String(r.ingresso));
      if (r.comunidade) q.set('comunidade', r.comunidade);
      return q.size ? `/sala/nova?${q}` : '/sala/nova';
    }
    case 'planos': {
      const q = new URLSearchParams();
      if (r.plano) q.set('plano', r.plano);
      if (r.ciclo === 'anual') q.set('ciclo', 'anual');
      return q.size ? `/planos?${q}` : '/planos';
    }
    case 'assinar': return `/planos/assinar?plano=${r.plano}` + (r.ciclo === 'anual' ? '&ciclo=anual' : '');
    case 'assinatura-ok': return r.plano ? `/planos/ok?plano=${r.plano}` : '/planos/ok';
    case 'meu-plano': return '/conta/plano';
    case 'indique': return '/indique';
    case 'como-jogar': return '/como-jogar';
    case 'termos': return '/termos';
    case 'privacidade': return '/privacidade';
    case 'limite': return '/limite';
    case 'indicacao': return `/c/${r.codigo}`;
    case 'simulado': return '/planos/simulado';
    case 'loja': return '/tentos';
    case 'extrato': return '/tentos/extrato';
    case 'compra-ok': return r.sessao ? `/tentos/ok?sessao=${encodeURIComponent(r.sessao)}` : '/tentos/ok';
    case 'compra-simulada': return '/tentos/simulado';
    case 'inicio': return r.aba === 'tentos' ? '/salas?aba=tentos' : '/';
  }
}

let navegouAqui = false; // houve navegação dentro do app nesta aba

export function ir(r: Rota, substituir = false): void {
  const c = caminhoDa(r);
  if (!substituir) navegouAqui = true;
  if (substituir) history.replaceState(null, '', c); else history.pushState(null, '', c);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Volta para a tela anterior do app; se a página foi aberta direto, vai para o início.
export function voltar(): void {
  if (navegouAqui) history.back(); else ir({ tela: 'inicio' }, true);
}

// Ouve antes dos efeitos das telas (useLayoutEffect): uma tela que redireciona ao montar não se perde.
export function useRota(): Rota {
  const [rota, setRota] = useState(() => lerRota(location.pathname, location.search));
  useLayoutEffect(() => {
    const f = () => setRota((a) => {
      const nova = lerRota(location.pathname, location.search);
      return JSON.stringify(nova) === JSON.stringify(a) ? a : nova;
    });
    window.addEventListener('popstate', f);
    f();
    return () => window.removeEventListener('popstate', f);
  }, []);
  return rota;
}

export function linkDaSala(codigo: string): string {
  return `${location.origin}/s/${codigo}`;
}

// O link que a pessoa compartilha leva o código de indicação dela: quem criar conta por ele dá +5 partidas a ela.
export function linkDeConviteDaSala(codigo: string, indicacao: string | null): string {
  return linkDaSala(codigo) + (indicacao ? `?c=${indicacao}` : '');
}
